<template>
  <!--
    Единая оболочка конфигуратора: порядок меню повторяет ход настройки
    кампании, а разрушительное закрытие намеренно остаётся последним пунктом.
  -->
  <div class="admission-config">
    <header class="admission-config__header">
      <div>
        <h1 class="admission-config__title">Конфигуратор приёмной комиссии</h1>
        <p class="admission-config__subtitle">
          Выберите раздел для настройки параметров приёмной кампании
        </p>
      </div>
    </header>

    <div class="admission-config__layout">
      <nav class="admission-config__navigation" aria-label="Разделы конфигуратора">
        <template v-for="(section, index) in visibleSections" :key="section.id">
          <a
            v-if="section.href"
            class="admission-config__navigation-item"
            :href="$model.wwwroot + section.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="admission-config__navigation-number">{{ index + 1 }}</span>
            <v-icon class="admission-config__navigation-icon" :icon="section.icon" size="20" />
            <span>{{ section.title }}</span>
          </a>
          <button
            v-else
            type="button"
            class="admission-config__navigation-item"
            :class="{ 'admission-config__navigation-item--active': activeSectionId === section.id }"
            :aria-current="activeSectionId === section.id ? 'page' : null"
            @click="selectSection(section.id)"
          >
            <span class="admission-config__navigation-number">{{ index + 1 }}</span>
            <v-icon class="admission-config__navigation-icon" :icon="section.icon" size="20" />
            <span>{{ section.title }}</span>
          </button>
        </template>
      </nav>

      <main class="admission-config__content">
        <component :is="activeSection.component" />
      </main>
    </div>
  </div>
</template>

<script>
import GeneralSection from './sections/GeneralSection.vue';
import PersonalDataSection from './sections/PersonalDataSection.vue';
import AddressSection from './sections/AddressSection.vue';
import CalendarSection from './sections/CalendarSection.vue';
import TargetSetSection from './sections/TargetSetSection.vue';
import SpecialtySection from './sections/SpecialtySection.vue';
import DocumentsSection from './sections/DocumentsSection.vue';
import PrivilegesSection from './sections/PrivilegesSection.vue';
import CloseAdmissionSection from './sections/CloseAdmissionSection.vue';
import EntranceExamStepsSection from './sections/EntranceExamStepsSection.vue';
import OtherSettingsSection from './sections/OtherSettingsSection.vue';
import PublicLinksSection from './sections/PublicLinksSection.vue';
import AdmissionConfigLogSection from './sections/AdmissionConfigLogSection.vue';

export default {
  name: 'AdmissionConfig',
  components: {
    GeneralSection,
    PersonalDataSection,
    AddressSection,
    CalendarSection,
    TargetSetSection,
    SpecialtySection,
    DocumentsSection,
    PrivilegesSection,
    CloseAdmissionSection,
    EntranceExamStepsSection,
    OtherSettingsSection,
    PublicLinksSection,
    AdmissionConfigLogSection
  },
  data() {
    return {
      activeSectionId: 'general',
      sections: [
        { id: 'general', title: 'Обязательные поля', icon: 'mdi-form-select', component: 'GeneralSection' },
        { id: 'personal-data', title: 'Защита персональных данных', icon: 'mdi-shield-account', component: 'PersonalDataSection' },
        { id: 'addresses', title: 'Адреса приёмной комиссии', icon: 'mdi-map-marker-outline', component: 'AddressSection' },
        { id: 'calendar', title: 'Календарь приёмной комиссии', icon: 'mdi-calendar-month-outline', component: 'CalendarSection' },
        { id: 'target-set', title: 'Целевой набор', icon: 'mdi-bullseye-arrow', component: 'TargetSetSection' },
        { id: 'specialties', title: 'Специальности', icon: 'mdi-school-outline', component: 'SpecialtySection' },
        { id: 'documents', title: 'Документы', icon: 'mdi-file-document-multiple-outline', component: 'DocumentsSection' },
        { id: 'privileges', title: 'Льготы', icon: 'mdi-account-star-outline', component: 'PrivilegesSection' },
        {
          id: 'certificate-subjects',
          title: 'Предметы аттестата',
          icon: 'mdi-book-open-page-variant-outline',
          href: '/blocks/manage_groups/#/dictionaries/certificateSubjects'
        },
        { id: 'exam-steps', title: 'Этапы вступительных испытаний', icon: 'mdi-format-list-numbered', component: 'EntranceExamStepsSection' },
        { id: 'other', title: 'Другие настройки', icon: 'mdi-cog-outline', component: 'OtherSettingsSection' },
        {
          id: 'public-links',
          title: 'Ссылки для публикации',
          icon: 'mdi-web',
          component: 'PublicLinksSection'
        },
        {
          id: 'actions-log',
          title: 'Журнал действий',
          icon: 'mdi-history',
          component: 'AdmissionConfigLogSection',
          adminOnly: true
        },
        { id: 'close-admission', title: 'Закрытие приёмной комиссии', icon: 'mdi-calendar-check-outline', component: 'CloseAdmissionSection' }
      ]
    };
  },
  computed: {
    visibleSections() {
      // Журнал содержит действия всех сотрудников и является отдельным
      // административным представлением, поэтому скрыт для обычных операторов.
      return this.sections.filter((section) => {
        return !section.adminOnly || Boolean(this.$model.isAdmin);
      });
    },
    activeSection() {
      return this.visibleSections.find((section) => {
        return section.id === this.activeSectionId;
      }) || this.visibleSections[0];
    }
  },
  methods: {
    selectSection(sectionId) {
      this.activeSectionId = sectionId;
    }
  }
};
</script>

<style scoped>
.admission-config {
  --config-border: #dfe5ec;
  --config-muted: #667085;
  --config-primary: #1565c0;
  width: 100%;
}

.admission-config__header {
  margin-bottom: 24px;
}

.admission-config__title {
  margin: 0;
  color: #1d2939;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.25;
}

.admission-config__subtitle {
  margin: 6px 0 0;
  color: var(--config-muted);
}

.admission-config__layout {
  display: grid;
  grid-template-columns: minmax(260px, 300px) minmax(0, 1fr);
  align-items: start;
  gap: 24px;
}

.admission-config__navigation {
  position: sticky;
  top: 16px;
  overflow: hidden;
  border: 1px solid var(--config-border);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.05);
}

.admission-config__navigation-item {
  display: grid;
  grid-template-columns: 26px 24px 1fr;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 9px 12px;
  border: 0;
  border-bottom: 1px solid #edf0f4;
  background: transparent;
  color: #344054;
  font: inherit;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.admission-config__navigation-item:last-child {
  border-bottom: 0;
}

.admission-config__navigation-item:hover {
  background: #f4f8fd;
  color: var(--config-primary);
}

.admission-config__navigation-item--active {
  background: #eaf3fc;
  color: var(--config-primary);
  font-weight: 600;
  box-shadow: inset 3px 0 0 var(--config-primary);
}

.admission-config__navigation-number {
  color: #98a2b3;
  font-size: 12px;
  font-weight: 600;
}

.admission-config__navigation-item--active .admission-config__navigation-number {
  color: var(--config-primary);
}

.admission-config__navigation-icon {
  justify-self: start;
}

.admission-config__content {
  min-width: 0;
}

@media (max-width: 900px) {
  .admission-config__layout {
    grid-template-columns: 1fr;
  }

  .admission-config__navigation {
    position: static;
    display: flex;
    overflow-x: auto;
    border-radius: 10px;
  }

  .admission-config__navigation-item {
    flex: 0 0 220px;
    border-right: 1px solid #edf0f4;
    border-bottom: 0;
  }

  .admission-config__navigation-item--active {
    box-shadow: inset 0 -3px 0 var(--config-primary);
  }
}
</style>
